/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';
import { ButtonProps } from '@material-ui/core';
import StringUtils from 'src/utils/StringUtils';
import { CreateButton } from 'src/components/UI';
import { CreateButtonProps } from 'src/components/UI/Buttons/CreateButton';
import BackDrop from 'src/components/BackDrop';
import { FormBodyAPI } from 'src/constants/fieldsTypes';
import {
  ModalWrapper,
  ModalWrapperProps,
} from 'src/components/Modals/ModalWrapper';
import { useFormSubmitShortcut } from 'src/hooks/useFormSubmitShortcut';

interface FormModalProps {
  entityName: string;
  title: string;
  buttonLabel: string;
  cancelButtonLabel: string;
  buttonEndIcon?: JSX.Element;
  description: string;
  actionLabel: string;
  open: boolean;
  createButton: boolean;
  createButtonProps: ButtonProps | Partial<CreateButtonProps>;
  showProgress: boolean;
  onClose: () => void;
  initialFormValue: any;
  shouldModalOpen: () => boolean;
  skipResetOnSubmit?: boolean;
  saveAction: (values: any) => Promise<void>;
  handleSave: (values: any) => Promise<void>;
  showSaveAction?: boolean;
  isOuterBackdrop?: boolean;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  modalProps?: ModalWrapperProps;
  formRef?: any;
}

export interface FormModalActionProps {
  useFormHook: () => {
    validationScheme: Yup.ObjectSchema;
    getSubmitValues?: (values?: any) => Promise<any>; // this method can be specified in the form to get any extra values that might be needed during submit api call
    FormRenderer: React.FC<FormBodyAPI & FormikHelpers<any>>;
    submitDisabledTooltip?: React.ReactNode;
  };
}

export const FormModal: React.FC<
  Partial<FormModalProps> & FormModalActionProps
> = React.memo(
  ({
    entityName,
    title,
    buttonLabel,
    cancelButtonLabel,
    buttonEndIcon,
    actionLabel,
    initialFormValue,
    shouldModalOpen,
    skipResetOnSubmit = false,
    saveAction,
    handleSave,
    showSaveAction = true,
    useFormHook,
    onClose,
    createButton = false,
    createButtonProps = {},
    open,
    isOuterBackdrop = false,
    validateOnChange = true,
    validateOnBlur = true,
    modalProps = {},
    formRef,
  }) => {
    const dispatch = useDispatch();
    const { validationScheme, getSubmitValues, FormRenderer } = useFormHook();
    const [modalState, setModalState] = React.useState({
      formValue: initialFormValue,
      open: false,
    });
    const cachedModalState = React.useMemo(
      () => ({
        ...modalState,
        formValue: initialFormValue,
      }),
      [initialFormValue, modalState],
    );

    const openModal = () => {
      if (!shouldModalOpen || shouldModalOpen()) {
        setModalState({
          formValue: initialFormValue,
          open: true,
        });
      }
    };

    const resetModal = () => {
      if (onClose) {
        onClose();
      }
      setModalState({
        formValue: initialFormValue,
        open: false,
      });
    };

    const closeModal = () => {
      resetModal();
    };

    /**
     * Send all submit values to the saving method passes in props. This can either be an
     * redux action which will be dispatched or a callback function
     * that is not dispatched. This method also looks for extra
     *  submit values that might not be in the form and needed to be formatted differently.
     * @param values the input values of the form that will be submitted
     * @returns void
     */
    const onFormSubmit = async (values: any, { setSubmitting, resetForm }) => {
      const extraValues = getSubmitValues ? await getSubmitValues(values) : {};
      const { isValid = true } = extraValues;

      if (isValid) {
        delete extraValues.isValid;
        const params = {
          ...values,
          fields: { ...values.fields, ...extraValues },
        };
        try {
          if (handleSave) {
            await handleSave(params);
          } else if (saveAction) {
            await dispatch(saveAction(params));
          }
          if (!skipResetOnSubmit) {
            if (resetForm) {
              resetForm();
            }
            resetModal();
          }
        } catch (err) {
          console.info('Submitting form error', err);
        }
      }
      setSubmitting(false);
    };

    const modalOpen = React.useMemo(
      () => cachedModalState.open || open,
      [cachedModalState, open],
    );
    return (
      <>
        {createButton && (
          <CreateButton
            htmlId={
              actionLabel
                ? `btn-create${StringUtils.PascalToSnackCase(actionLabel)}`
                : `btn-create-${entityName}`
            }
            onClick={openModal}
            {...createButtonProps}
          >
            {actionLabel || 'New'}
          </CreateButton>
        )}

        {modalOpen && (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={cachedModalState.formValue}
            validationSchema={validationScheme}
            onSubmit={onFormSubmit}
            validateOnChange={validateOnChange}
            validateOnBlur={validateOnBlur}
          >
            {(props) => {
              useFormSubmitShortcut(props.handleSubmit);

              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              return (
                <ModalWrapper
                  title={
                    title ||
                    (cachedModalState.formValue.id
                      ? `Update ${entityName}`
                      : `New ${entityName}`)
                  }
                  open={modalOpen}
                  onClose={() => {
                    if (props.resetForm) {
                      props.resetForm();
                    }
                    closeModal();
                  }}
                  onSuccess={props.submitForm}
                  isLoading={props.isSubmitting}
                  positiveAction={showSaveAction}
                  successButtonLabel={buttonLabel}
                  successButtonEndIcon={buttonEndIcon}
                  cancelButtonLabel={cancelButtonLabel}
                  {...modalProps}
                >
                  <form onSubmit={props.handleSubmit}>
                    {FormRenderer(props)}
                  </form>
                </ModalWrapper>
              );
            }}
          </Formik>
        )}

        {isOuterBackdrop && <BackDrop isModalOpen />}
      </>
    );
  },
);
